import React from 'react';
import useInputController, { isEmpty, createInputValidator } from '../hooks/useInputController';

import type { InputHTMLAttributes } from 'react';

const NUMBER_REGEXP = /^\s*(-|\+)?(\d+|(\d*(\.\d*)))\s*$/;

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> & {
	type: 'number';
	value: number;
	onChange: (value: number) => void;
	debounce?: number;
};

const NumberInput = React.forwardRef<HTMLInputElement, Props>(
	({ value, onChange, debounce, onBlur: externalOnBlur, ...inputProps }, ref) => {
		const controlled = useInputController<number>(value, onChange, {
			name: inputProps.name,
			debounce,
			format: v => (isEmpty(v) ? '' : String(v)),
			parse: v => {
				if (isEmpty(v)) return null as unknown as number;
				if (NUMBER_REGEXP.test(v)) return parseFloat(v);
				return undefined as unknown as number;
			},
			validate: createInputValidator(inputProps)
		});

		function onBlur(event: React.FocusEvent<HTMLInputElement>) {
			controlled.onBlur();
			externalOnBlur?.(event);
		}

		return (
			<input
				{...inputProps}
				type="number"
				value={controlled.value}
				onChange={controlled.onChange}
				onBlur={onBlur}
				ref={ref}
			/>
		);
	}
);

export default NumberInput;
